import { FiAlertTriangle, FiEdit3 } from 'react-icons/fi';
import { SectionHelpTitle } from './SectionHelp';
import type { LegalCaseRisk } from '../../../../types/LegalCase';
import {
    classificationBadgeClass,
    hasProvision,
    hasRiskData,
    isOffSuggestion,
} from './cpc25Risk';

interface Cpc25RiskCardProps {
    risk: LegalCaseRisk | null;
}

// Card read-only no Resumo do caso: mostra a classificação CPC 25, a perda estimada (% e R$) e
// a provisão. A provisão é calculada pela regra (só passivo provável) no backend; quando há
// ajuste manual (override), sinaliza e mostra os dois valores. A edição é no form do caso.
const Cpc25RiskCard = ({ risk }: Cpc25RiskCardProps) => {
    // Sem classificação nem percentual não há risco a mostrar (o caso ainda não foi avaliado).
    if (!hasRiskData(risk)) return null;

    return (
        <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
            <SectionHelpTitle title="Risco CPC 25" topic="case.risk" />
            <div className="rounded-lg bg-gray-50 dark:bg-slate-900 p-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Classificação</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${classificationBadgeClass(risk.cpc25Classification)}`}>
                        {risk.cpc25ClassificationFormatted || 'Não classificado'}
                    </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        Perda estimada
                        {isOffSuggestion(risk) && (
                            <FiAlertTriangle
                                className="text-amber-500"
                                size={13}
                                title="Percentual fora do padrão sugerido para a classificação"
                            />
                        )}
                    </span>
                    <span className="dark:text-white">
                        {risk.estimatedLossPercentFormatted || '—'}
                        {risk.estimatedLossAmountFormatted ? ` (${risk.estimatedLossAmountFormatted})` : ''}
                    </span>
                </div>
                <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-200">Provisão</span>
                    <span className={`font-semibold ${hasProvision(risk)
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-gray-700 dark:text-gray-200'}`}>
                        {risk.provisionAmountFormatted || 'R$ 0,00'}
                    </span>
                </div>
                {risk.provisionOverridden && (
                    <div className="flex items-start justify-between text-xs gap-2">
                        <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FiEdit3 size={12} /> Ajustada manualmente
                        </span>
                        <span className="text-right text-gray-500 dark:text-gray-400">
                            calculada: {risk.provisionCalculatedFormatted || 'R$ 0,00'}
                        </span>
                    </div>
                )}
                {risk.provisionOverridden && risk.provisionOverrideReason && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                        {risk.provisionOverrideReason}
                    </p>
                )}
            </div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                Provisão reconhecida apenas para caso passivo classificado como provável (CPC 25).
            </p>
        </div>
    );
};

export default Cpc25RiskCard;
